<script>
  import { debounce } from 'lodash';

  const MIN_SEARCH_LENGTH = 4;

  export default {
    name: 'AddFriendsInput',

    data: () => ({
      search: '',
      isFocused: false,
      selectedUser: '',
      hoveredUser: '',
      loading: false,
    }),

    computed: {
      menuProps() {
        return {
          value: this.isFocused,
          dark: true,
          offsetY: true,
          contentClass: `golden-select-menu ${this.isEnoughCharacters ? '' : 'd-none'}`,
          nudgeBottom: '4px',
          hideOnClick: false,
        };
      },
      isEnoughCharacters() {
        return this.search?.length >= MIN_SEARCH_LENGTH;
      },
      displayedItems() {
        if (!this.search || this.search?.length < MIN_SEARCH_LENGTH) {
          return [];
        }

        if (this.selectedUser && this.selectedUser === this.search) {
          return [this.selectedUser];
        }

        return this.$store.getters.getPossibleFriends || [];
      },
    },

    watch: {
      search: {
        handler() {
          this.loading = true;
          this.updateSearch();
        },
        immediate: true,
      },
    },

    methods: {
      handleBlur() {
        this.isFocused = false;
        this.search = '';
        this.selectedUser = '';
        this.$refs.autocompleteRef?.blur();
      },
      updateSearch: debounce(function () {
        if (!this.search) {
          this.loading = false;

          return;
        }

        if (this.selectedUser && this.selectedUser !== this.lastsearch) {
          this.lastsearch = this.selectedUser;
          this.search = this.selectedUser;
        }

        if (this.search !== this.lastsearch) {
          const blockedFrom = this.$store.getters.getUserBlockedFromList || [];
          const friends = this.$store.getters.getAcceptedFriendsList || [];
          const userFriendsSent = this.$store.getters.getSentFriendsList || [];
          const userFriendsReceived = this.$store.getters.getReceivedFriendsList || [];
          const blockedAndFriendsDisplayNames = [...blockedFrom, ...friends].map((a) => a.displayName);
          const usersInProcess = [
            ...userFriendsSent.map((a) => a.displayName),
            ...userFriendsReceived.map((a) => a.displayName),
          ];
          const currentUser = this.$store.getters.getuser.displayName;
          const searchString = this.search?.trim();
          const excludeUsers = [
            ...blockedAndFriendsDisplayNames.filter((a) => a.toLowerCase().includes(searchString)),
            ...usersInProcess.filter((a) => a.toLowerCase().includes(searchString)),
            currentUser,
          ];

          this.selectedUser = '';
          this.lastsearch = this.search;

          this.$store.dispatch('searchDisplayName', {
            searchString: searchString?.replaceAll(' ', ''),
            excludeUsers: excludeUsers,
          });
        }

        this.loading = false;
      }, 300),

      async addFriend(displayName) {
        if (!displayName) {
          return;
        }

        const response = await this.$store.dispatch('sendfriendrequest', displayName);

        this.selectedUser = '';
        this.search = '';

        const text = response.status === 'ERROR' ? response.message : 'Your friend request was sent successfully!';

        await this.$store.dispatch('showNotificationTooltip', text);
      },

      preventSelection(event, user) {
        event.preventDefault();
        this.hoveredUser = user;
        this.selectedUse = user;
      },
    },
  };
</script>

<template>
  <v-autocomplete
    ref="autocompleteRef"
    :value="''"
    :hint="isFocused ? 'Please type at least 4 characters' : ''"
    :items="displayedItems"
    :loading="loading"
    :menu-props="menuProps"
    :no-filter="true"
    :search-input.sync="search"
    class="friends-input"
    :class="{ 'button-like': !isFocused }"
    :placeholder="isFocused ? 'Search for a friend...' : 'Add a Friend'"
    prepend-inner-icon="mdi-account-plus"
    append-icon=""
    color="gold"
    hide-details
    @blur="handleBlur"
    @focus="isFocused = true"
  >
    <template #item="{ item }">
      <v-list-item
        class="d-flex justify-space-between w-full friend-item"
        @click.prevent.stop="preventSelection($event, item)"
        @mouseover="() => (hoveredUser = item)"
        @mouseleave="() => (hoveredUser = '')"
      >
        <span>{{ item }}</span>
        <v-btn class="add-friend-btn" :class="{ visible: hoveredUser === item }" @click="addFriend(item)">
          Add as Friend
        </v-btn>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<style scoped lang="scss">
  .friends-input {
    padding-top: 0 !important;
    margin-top: 0 !important;
    transition: all 0.3s;
    border-radius: 8px;

    &.button-like {
      cursor: pointer !important;
      border-radius: 8px;

      &::v-deep {
        .v-input__control {
          .v-input__slot {
            background: var(--v-gold-base);

            input {
              transform-style: preserve-3d;
              transform: translate3d(0, 0, 0);
              padding-left: 0 !important;
              font-weight: 600;

              &::placeholder {
                font-size: 16px;
                color: #1c0630;
              }
            }
          }

          .v-input__prepend-inner {
            padding-left: 30px !important;
            padding-right: 8px !important;
            flex-basis: 60%;

            .v-input__icon {
              justify-content: flex-end;
            }
          }
        }
      }
    }

    &:not(.button-like) {
      &::v-deep {
        .v-input__control {
          .v-input__slot {
            outline: 1px solid var(--v-gold-base);
          }
        }
      }
    }

    &::v-deep {
      .v-input__control {
        .v-input__slot {
          transition: all 0.3s;
          background: rgba(0, 0, 0, 0.2);
          padding-left: 0 !important;

          &::after,
          &::before {
            display: none;
          }

          input {
            padding-left: 8px !important;
            padding-top: 2px !important;
            font-size: 16px !important;
            color: #a19caa;
          }

          .v-select__selection {
            color: white;
          }
        }

        .v-input__prepend-inner {
          padding: 10px 16px !important;
          transition: all 0.3s;
          background: var(--v-gold-base);
          margin: 0 !important;
          height: 45px !important;
          border-top-left-radius: 8px;
          border-bottom-left-radius: 8px;
          display: flex;
          justify-content: center;
          align-items: center;

          .v-icon {
            color: #1c0630 !important;
          }
        }
      }
    }
  }
</style>
